<template>
  <SmallOutput
    v-if="isShowSmallOutput"
    :resultsCount
    :options
    @selectedChipId="handleSelectedChipId"
  />
</template>

<script setup lang="ts">
  import { SmallOutput } from '@/entities/filter/small-output';
  import { useExcursionFiltersStore } from '@/entities/filter/store/excursion';
  import type { UiChipsProps } from '@experience/core-chips-ui';
  import { useExcursionFiltersTitles } from '../../composables/excursion/useExcursionFilterTitles';
  import type { CityTag } from '@/shared/api/experience';
  import { useExcursionSmallOutput } from '@/features/smallOutput/excursion';
  import type { CurrencyCode } from '@/shared/lib/currency';

  interface Props {
    tags: CityTag[];
    currencyCode: CurrencyCode;
  }

  const props = defineProps<Props>();

  const filtersStore = useExcursionFiltersStore();
  const { showSmallOutput } = useExcursionSmallOutput();

  type ChipsModelValue = UiChipsProps['modelValue'];

  const { filterChips, resetters } = useExcursionFiltersTitles(props.currencyCode);

  const isShowSmallOutput = computed(() => showSmallOutput.value && resultsCount.value === 0);

  const options = computed(() => {
    const chipOptions = [...filterChips.value];

    const foundRubric = props.tags.find((item) => {
      const currentSlug = (item as CityTag)?.slug;

      return currentSlug === filtersStore.searchState.tag__slugs;
    });

    const foundRubricSlug = (foundRubric as CityTag)?.slug || '';

    if (foundRubric && foundRubricSlug !== 'all') {
      chipOptions.push({
        value: 'chip',
        label: foundRubric.name || '',
      });
    }

    return chipOptions;
  });

  const resultsCount = computed(() => filtersStore.searchTotalCount || 0);

  const getChipKey = (chipId: ChipsModelValue) => {
    const value = Array.isArray(chipId) ? chipId[0] : chipId;

    if (typeof value === 'string' || typeof value === 'number') {
      return String(value);
    }

    if (value && typeof value === 'object') {
      const { value: optionValue } = value;

      return typeof optionValue === 'string' || typeof optionValue === 'number' ? String(optionValue) : null;
    }

    return null;
  };

  const handleSelectedChipId = (chipId: ChipsModelValue) => {
    const chipKey = getChipKey(chipId);

    if (!chipKey) {
      return;
    }

    const resetterEntry = Object.entries(resetters).find(([key]) => key === chipKey);

    if (resetterEntry) {
      resetterEntry[1]();
    }

    if (chipKey === 'chip') {
      filtersStore.reset('tag__slugs');
    }

    filtersStore.search();
  };
</script>
